/**
 * BETA TELEMETRY AND DIAGNOSTICS — CLIENT (Phase 13B, spec §58C/§58D).
 *
 * What this sends is deliberately small: a NAMED event, the area of the
 * application it happened in, and a few scalar values. It never sends
 * project content, financial figures, names, addresses, keystrokes, mouse
 * movement or screenshots — and the server drops anything outside its own
 * allowlist regardless of what a client claims.
 *
 * Nothing here may ever break the work it describes: every call is
 * fire-and-forget, batched, and silent on failure.
 */

export type TelemetryName =
  | "session.active"
  | "area.viewed"
  | "project.created"
  | "project.status-changed"
  | "project.deleted"
  | "design.created"
  | "design.edited"
  | "feature.created"
  | "package.applied"
  | "baseline.created"
  | "change-order.created"
  | "change-order.approved"
  | "expense.created"
  | "purchase.recorded"
  | "mileage.recorded"
  | "product.selected"
  | "report.generated"
  | "report.printed"
  | "export.performed"
  | "report.saved-to-project"
  | "sale.recorded"
  | "project.closed"
  /* Spec §86: interactions the funnel wants to see. Milestones that the
   * DATA can evidence (a project, scope, a priced estimate, a quote) are
   * derived by the server from the workspace itself and are deliberately
   * NOT client events — a client cannot claim progress it did not make. */
  | "estimate_viewed"
  | "report_exported"
  | "onboarding_step_viewed";

interface QueuedEvent {
  event: string;
  area?: string;
  occurredAt: string;
  metadata?: Record<string, string | number | boolean>;
}

const queue: QueuedEvent[] = [];
let enabled = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** Enabled only in the hosted deployment; the desktop records nothing. */
export function enableTelemetry(on: boolean): void {
  enabled = on;
  if (!on) queue.length = 0;
}

export function isTelemetryEnabled(): boolean {
  return enabled;
}

export function track(
  event: TelemetryName,
  metadata?: Record<string, string | number | boolean>,
  area?: string,
): void {
  if (!enabled) return;
  queue.push({ event, area, occurredAt: new Date().toISOString(), metadata });
  if (queue.length >= 20) {
    void flushTelemetry();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => void flushTelemetry(), 5000);
  }
}

export async function flushTelemetry(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!enabled || queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  try {
    await fetch("/api/telemetry", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ events }),
    });
  } catch {
    // A lost batch is a lost batch. It must never surface to the user.
  }
}

/* ------------------------------------------------------------------ */
/* Activation (spec §86)                                               */
/* ------------------------------------------------------------------ */

let activationTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Ask the server to re-read this account's own projects and record any
 * activation milestone they now evidence. It carries NO claim — no project
 * id, no milestone name, no figure — so a client cannot fabricate progress;
 * the server derives everything from the workspace the session already
 * owns. Coalesced, fire-and-forget, hosted only.
 */
export function reportActivationSeen(): void {
  if (!enabled) return;
  if (activationTimer) clearTimeout(activationTimer);
  activationTimer = setTimeout(() => {
    activationTimer = null;
    void fetch("/api/activation/observe", { method: "POST", credentials: "same-origin" }).catch(() => undefined);
  }, 1500);
}

/* ------------------------------------------------------------------ */
/* Diagnostics                                                         */
/* ------------------------------------------------------------------ */

export type DiagnosticCategory =
  | "render"
  | "request"
  | "persistence"
  | "attachment"
  | "provider"
  | "report"
  | "export"
  | "unknown";

/**
 * Report a failure so the owner can see it without the tester having to
 * describe it. The message is trimmed and anything secret-shaped is removed
 * here as well as on the server.
 */
export async function reportDiagnostic(input: {
  category: DiagnosticCategory;
  message: string;
  operation?: string;
  detail?: string;
}): Promise<string> {
  if (!enabled) return "";
  const body = {
    category: input.category,
    operation: input.operation ?? "",
    route: currentRoute(),
    message: scrub(input.message).slice(0, 300),
    detail: scrub(input.detail ?? "").slice(0, 2000),
  };
  try {
    const response = await fetch("/api/diagnostics", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    // The server issues a short REFERENCE for the record it just stored, so
    // the tester can quote it instead of describing a stack trace. It names
    // a row and grants nothing.
    const payload = (await response.json().catch(() => ({}))) as { reference?: string };
    return typeof payload.reference === "string" ? payload.reference : "";
  } catch {
    /* diagnostics are best effort, always */
    return "";
  }
}

function currentRoute(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.hash}`.slice(0, 80);
}

/** Never let a token, key or password leave the browser in a report. */
export function scrub(text: string): string {
  return String(text)
    .replace(/(api[_-]?key|token|password|secret|authorization|cookie|bearer)\s*[:=]\s*\S+/gi, "$1=[redacted]")
    .replace(/\b[A-Za-z0-9_-]{40,}\b/g, "[redacted]");
}

/** Catch what the application does not: unhandled errors and rejections. */
export function installGlobalDiagnostics(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (event) => {
    void reportDiagnostic({
      category: "render",
      message: event.message || "Unhandled error",
      operation: "window.error",
      detail: `${event.filename ?? ""}:${event.lineno ?? 0}`,
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    void reportDiagnostic({
      category: "unknown",
      message: reason instanceof Error ? reason.message : String(reason),
      operation: "unhandledrejection",
    });
  });
  window.addEventListener("beforeunload", () => {
    void flushTelemetry();
  });
}
